/**
 * PowerFlowDiagram — SVG animated power flow between nodes.
 *
 * Layout (cross/star):
 *
 *        ☀ Solar
 *          |
 * ⚡ Grid ─[SITE]─ 🏠 Load
 *          |
 *        🔋 Battery
 */

import './PowerFlowDiagram.css';

export interface PowerFlowData {
  solar: number;     // kW
  grid: number;      // kW (positive = import, negative = export)
  battery: number;   // kW (positive = charging, negative = discharging)
  load: number;      // kW (consumption)
}

interface NodeProps {
  x: number;
  y: number;
  icon: string;
  label: string;
  value: number;
  unit?: string;
  color: string;
}

const W = 360;
const H = 300;
const CX = W / 2;
const CY = H / 2;
const NODE_R = 30;
const BRANCH_LEN = 90;

const SOLAR_POS    = { x: CX,             y: CY - BRANCH_LEN };
const GRID_POS     = { x: CX - BRANCH_LEN, y: CY };
const LOAD_POS     = { x: CX + BRANCH_LEN, y: CY };
const BATTERY_POS  = { x: CX,             y: CY + BRANCH_LEN };

function FlowNode({ x, y, icon, label, value, unit = 'kW', color }: NodeProps) {
  return (
    <g className="pf-node">
      {/* Node circle */}
      <circle cx={x} cy={y} r={NODE_R} fill={`${color}18`} stroke={color} strokeWidth="2" />
      {/* Icon (emoji as text) */}
      <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="20" className="pf-node__icon">
        {icon}
      </text>
      {/* Label */}
      <text x={x} y={y + NODE_R + 14} textAnchor="middle" fontSize="11" fill="var(--text-muted)" fontFamily="var(--font-main)">
        {label}
      </text>
      {/* Value */}
      <text x={x} y={y + NODE_R + 27} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--text-main)" fontFamily="var(--font-main)" className="pf-node__value">
        {value.toFixed(1)} {unit}
      </text>
    </g>
  );
}

interface LineProps {
  x1: number; y1: number;
  x2: number; y2: number;
  color: string;
  active: boolean;
  animId: string;
}

function FlowLine({ x1, y1, x2, y2, color, active, animId }: LineProps) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  /* shorten line to not overlap node circles */
  const shrink = NODE_R + 4;
  const ratio = shrink / len;
  const ax1 = x1 + dx * ratio;
  const ay1 = y1 + dy * ratio;
  const ax2 = x2 - dx * ratio;
  const ay2 = y2 - dy * ratio;

  return (
    <g>
      {/* Background track */}
      <line x1={ax1} y1={ay1} x2={ax2} y2={ay2} stroke="var(--gray-200)" strokeWidth="3" strokeLinecap="round" />
      {/* Animated flow line */}
      {active && (
        <line
          x1={ax1} y1={ay1} x2={ax2} y2={ay2}
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="6 8"
          className={`pf-flow-line pf-flow-line--${animId}`}
        />
      )}
    </g>
  );
}

interface PowerFlowDiagramProps {
  data?: PowerFlowData;
}

const MOCK: PowerFlowData = {
  solar:   45.2,
  grid:    12.1,
  battery:  8.4,
  load:    53.6,
};

export default function PowerFlowDiagram({ data = MOCK }: PowerFlowDiagramProps) {
  return (
    <div className="pf-card card">
      <div className="pf-card__header">
        <span className="pf-card__title">Power Flow</span>
        <span className="pf-card__live">● Live</span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        className="pf-svg"
        aria-label="Power flow diagram"
      >
        {/* SVG drop shadow filter for center node */}
        <defs>
          <filter id="node-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.12)" />
          </filter>
        </defs>

        {/* Flow lines */}
        <FlowLine x1={SOLAR_POS.x}    y1={SOLAR_POS.y}    x2={CX} y2={CY} color="#2F80FF" active={data.solar > 0}   animId="solar" />
        <FlowLine x1={GRID_POS.x}     y1={GRID_POS.y}     x2={CX} y2={CY} color="#9CA3AF" active={Math.abs(data.grid) > 0} animId="grid" />
        <FlowLine x1={LOAD_POS.x}     y1={LOAD_POS.y}     x2={CX} y2={CY} color="#27AE60" active={data.load > 0}    animId="load" />
        <FlowLine x1={BATTERY_POS.x}  y1={BATTERY_POS.y}  x2={CX} y2={CY} color="#F2994A" active={Math.abs(data.battery) > 0} animId="battery" />

        {/* Center node */}
        <rect
          x={CX - 26} y={CY - 26}
          width={52} height={52}
          rx="10"
          fill="var(--bg-card)"
          stroke="var(--gray-200)"
          strokeWidth="1.5"
          filter="url(#node-shadow)"
        />
        <text x={CX} y={CY - 4} textAnchor="middle" fontSize="9" fontWeight="600" fill="var(--text-muted)" fontFamily="var(--font-main)" letterSpacing="0.06em">
          SITE
        </text>
        <text x={CX} y={CY + 8} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--primary-blue)" fontFamily="var(--font-main)">
          ⚡
        </text>

        {/* Branch nodes */}
        <FlowNode {...SOLAR_POS}   icon="☀️" label="Solar"    value={data.solar}   color="#2F80FF" />
        <FlowNode {...GRID_POS}    icon="⚡" label="Grid"     value={Math.abs(data.grid)}   color="#9CA3AF" />
        <FlowNode {...LOAD_POS}    icon="🏠" label="Load"     value={data.load}    color="#27AE60" />
        <FlowNode {...BATTERY_POS} icon="🔋" label="Battery"  value={Math.abs(data.battery)} color="#F2994A" />
      </svg>
    </div>
  );
}
